import clsx from 'clsx'
import { useCallback, useContext, useEffect, useRef, type RefObject } from 'react'

import { selectActiveMatterArtifactSelection } from '@/agent-v2/artifact-selection/store/selectors'
import { selectAgentV2MatterArtifactEditProposalReviewContext } from '@/agent-v2/artifact-edit-review/store/selectors'
import AgentV2ArtifactEditor from '@/agent-v2/artifact-editor/components/AgentV2ArtifactEditor'
import { selectAgentV2TransportCanStopOrAbort } from '@/agent-v2/transport/store/selectors'
import { AgentV2TransportActions } from '@/agent-v2/transport/store/slice'
import ErrorBoundary from '@/components/error/ErrorBoundary'
import ErrorCard from '@/components/error/ErrorCard'
import ContentViewerErrorState from '@/content-viewer/components/common/ContentViewerErrorState'
import ContentViewerLoadingSpinner from '@/content-viewer/components/common/ContentViewerLoadingStates'
import { AuthContext } from '@/context/auth-context'
import { selectChatPanelOpen } from '@/drafting/workspace/store/selectors'
import ReadOnlyMatterArtifact from '@/matter-artifacts/components/ReadOnlyMatterArtifact'
import { useLoadMatterArtifactVersion } from '@/matter-artifacts/hooks/useLoadMatterArtifactVersion'
import { MatterArtifactVersionStatus } from '@/matter-artifacts/schemas'
import { selectCurrentMatterArtifactVersionFromVersionId, selectMatterArtifactVersionById } from '@/matter-artifacts/store/selectors'
import { setSideNavCollapsed } from '@/store/slices/ui-state.slice'
import type { RootState } from '@/store/store'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { useDraftExport } from '../context/draft-export-context'
import DraftHeader from './DraftHeader'
import FloatingPendingEditReview, { PendingEditKind, type PendingEditState } from './FloatingPendingEditReview'
import './draft-panel.css'

type DraftPanelProps = {
  conversationId: string
  matterArtifactVersionId: string
  isSmallScreen?: boolean
  chatPanelRef?: RefObject<HTMLDivElement | null>
}

/** The draft document in the drafting workspace. */
export default function DraftPanel({ conversationId, matterArtifactVersionId, isSmallScreen = false, chatPanelRef }: DraftPanelProps) {
  const dispatch = useAppDispatch()
  const { user } = useContext(AuthContext)
  const { templateButton, editorRef } = useDraftExport()
  const sideNavCollapsed = useAppSelector((state: RootState) => state.uiState.sideNavCollapsed)
  const chatPanelOpen = useAppSelector((state: RootState) => selectChatPanelOpen(state, { draftArtifactVersionId: matterArtifactVersionId }))
  const chatPanelDocked = chatPanelOpen && !isSmallScreen
  const matterArtifactVersion = useAppSelector((state: RootState) => selectMatterArtifactVersionById(state, matterArtifactVersionId))
  const currentMatterArtifactVersion = useAppSelector((state: RootState) => selectCurrentMatterArtifactVersionFromVersionId(state, matterArtifactVersionId))
  const pendingEditContext = useAppSelector((state: RootState) =>
    selectAgentV2MatterArtifactEditProposalReviewContext(state, {
      conversationId,
      sourceMatterArtifactVersionId: currentMatterArtifactVersion?.id,
    })
  )
  const activeMatterSelection = useAppSelector((state: RootState) => selectActiveMatterArtifactSelection(state, { conversationId }))
  const canCancelSelectedEdit = useAppSelector((state: RootState) => selectAgentV2TransportCanStopOrAbort(state, { conversationId }))
  // Feeds the editor so the passage under review stays highlighted (the attached selection is consumed at submit).
  const pendingMatterSelection = pendingEditContext?.proposal.editorRange
    ? {
        selection: pendingEditContext.matterArtifactSelection,
        editorRange: pendingEditContext.proposal.editorRange,
      }
    : null
  const { hasError: versionLoadError, retry: retryVersionLoad } = useLoadMatterArtifactVersion(matterArtifactVersionId)
  const canEdit = Boolean(user?.uid && currentMatterArtifactVersion?.created_by_user_id === user.uid)
  const draftPanelRef = useRef<HTMLDivElement>(null)
  const selectedEditIsLoading = pendingEditContext === null && activeMatterSelection?.matter_artifact_id === currentMatterArtifactVersion?.matter_artifact_id
  const cancelSelectedEdit = useCallback(() => {
    dispatch(AgentV2TransportActions.stopTurnRequested({ conversationId }))
  }, [conversationId, dispatch])
  let pendingEdit: PendingEditState | null = null
  if (pendingEditContext) {
    pendingEdit = { kind: PendingEditKind.REVIEW, context: pendingEditContext }
  } else if (selectedEditIsLoading) {
    pendingEdit = { kind: PendingEditKind.LOADING, canCancel: canCancelSelectedEdit, onCancel: cancelSelectedEdit }
  }

  // Collapse the side nav on entry to give the draft room. On exit, restore it only if it was open before entry: a side nav the user
  // already had collapsed stays collapsed, while one we collapsed for drafting (or they toggled shut meanwhile) reopens.
  const wasSideNavCollapsedBeforeEntry = useRef(sideNavCollapsed)
  useEffect(() => {
    const collapsedBeforeEntry = wasSideNavCollapsedBeforeEntry.current
    dispatch(setSideNavCollapsed(true))
    return () => {
      if (!collapsedBeforeEntry) dispatch(setSideNavCollapsed(false))
    }
  }, [dispatch])

  return (
    // With the chat panel docked the document sits in a card beside it; otherwise it fills the workspace on its own.
    <div
      className={
        chatPanelDocked
          ? 'flex min-w-0 flex-grow flex-col overflow-hidden rounded-lg border border-brand-neutral-300 bg-white'
          : 'flex min-w-0 flex-grow overflow-hidden'
      }
    >
      {chatPanelDocked && <DraftHeader layout="card" />}
      {/* Keyed so adding the card header never remounts the editor. */}
      <div
        key="draft-body"
        className={clsx(
          'flex min-h-0 min-w-0 flex-1 overflow-hidden',
          // Keeps a gutter either side of the full-width column once the workspace is narrower than its max width.
          !chatPanelDocked && !isSmallScreen && 'px-4'
        )}
      >
        <div
          ref={draftPanelRef}
          className={clsx(
            'draft-panel flex h-full w-full flex-col',
            chatPanelDocked && 'draft-panel--docked',
            // Small screens frame the document as a page with space around it; a wide full-width document is a flush centered column (max width is from figma).
            isSmallScreen ? 'draft-panel--page' : !chatPanelDocked && 'mx-auto max-w-[928px] border-x border-brand-neutral-200'
          )}
        >
          {/* Ownership can't be resolved until the version loads, so hold on a spinner (or an error if
              the fetch fails) rather than flashing the read-only view and its owner banner to the owner
              on a cold load. Once loaded, only the owner can edit; collaborators get the read-only view. */}
          {!matterArtifactVersion ? (
            versionLoadError ? (
              <ContentViewerErrorState message="Could not load this draft." onRetry={retryVersionLoad} />
            ) : (
              <ContentViewerLoadingSpinner size="large" />
            )
          ) : !currentMatterArtifactVersion ? (
            // A generating version becomes the artifact's head once its status poll completes, so it waits.
            // Any other version without a head has none on the way. No known path produces that state, since the
            // conversation detail always carries the head, so this is a guard against hanging on a silent spinner.
            matterArtifactVersion.status === MatterArtifactVersionStatus.GENERATING ? (
              <ContentViewerLoadingSpinner size="large" />
            ) : (
              <ContentViewerErrorState message="Could not load this draft." />
            )
          ) : (
            // A crashed editor degrades to a contained retry card instead of unmounting the route.
            // Keyed so switching drafts discards a stale crash state along with the document.
            <ErrorBoundary
              key={matterArtifactVersionId}
              fallback={(retry) => (
                <div className="flex h-full w-full items-center justify-center p-4">
                  <ErrorCard code="500" title="Draft error" message="The draft editor ran into a problem." actionLabel="Retry" onClick={retry} />
                </div>
              )}
            >
              {canEdit ? (
                <AgentV2ArtifactEditor
                  ref={editorRef}
                  conversationId={conversationId}
                  matterArtifactVersionId={matterArtifactVersionId}
                  pendingMatterSelection={pendingMatterSelection}
                  templateButton={templateButton}
                />
              ) : (
                <ReadOnlyMatterArtifact conversationId={conversationId} matterArtifactVersionId={currentMatterArtifactVersion.id} />
              )}
            </ErrorBoundary>
          )}
        </div>
        {canEdit && currentMatterArtifactVersion && pendingEdit && (
          <FloatingPendingEditReview
            draftRef={draftPanelRef}
            chatPanelRef={chatPanelDocked ? chatPanelRef : undefined}
            conversationId={conversationId}
            pendingEdit={pendingEdit}
          />
        )}
      </div>
    </div>
  )
}
